"use client";

import type { ReactNode, FC, Ref } from "react";
import {
  Container,
  type ContainerProps,
  type ClassNameProp,
  Text,
  Flex,
  Spacing,
  mergeRefs,
  Heading,
  type HouseRecord,
  type GalleryProps,
  Badge,
  Button,
  IconButton,
  ValueParser,
  type ChildrenProp,
  Message,
} from "@huub-web/ui";
import { mergeClasses } from "@huub-web/utils";

import { Section } from "../Section";
import { useContentMenuSection } from "../../hooks/useContentMenuSection";

import { HouseHeader } from "../HouseHeader";
import { GaugeCard } from "../GaugeCard";
import { SummaryLoading } from "../Analysis/components/SummaryLoading";

import {
  photosButtonWrapperStyle,
  photosIconButtonWrapperStyle,
} from "./AnalysisSummary.css";

export type AnalysisSummaryProps = ClassNameProp &
  ChildrenProp & {
    /**
     * Record to display
     **/
    record?: HouseRecord;
    /**
     * Whether the record is unlisted
     **/
    unlisted?: boolean;
    /**
     * CTAs to display
     **/
    CTAs?: {
      maximumMortgage?: ReactNode;
      chance?: ReactNode;
    };
    /**
     * Title to display
     **/
    title?: string;
    /**
     * Whether the record is saved
     **/
    saved?: boolean;
    /**
     * AI generated summary content to display
     **/
    summary?: string;
    /**
     * Images to display
     **/
    images?: GalleryProps["images"];
    /**
     * Whether the component is loading
     **/
    loading?: boolean;
    /**
     * Container size
     **/
    container?: ContainerProps["size"];
    /**
     * Grade of the house (0-100)
     **/
    grade?: number;
    /**
     * Impression of the house
     **/
    impression?: string;
    /**
     * Whether the component is disabled
     **/
    disabled?: boolean;
    /**
     * Whether the record is savable
     **/
    savable?: boolean;
    /**
     * Callback function to handle save toggle
     **/
    onSaveToggle?: () => void;
    /**
     * Callback function to handle images modal open
     **/
    onImagesModalOpen?: () => void;
    /**
     * Action button for the Interior Designer entry point
     */
    interiorDesignerAction?: ReactNode;
    /**
     * Loading children to display
     **/
    loadingChildren?: ReactNode;
    /**
     * Whether the component has no data
     **/
    noData?: boolean;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

/**
 * AnalysisSummary component displays a summary section
 * with an optional heading and description
 */
export const AnalysisSummary: FC<AnalysisSummaryProps> = ({
  container = "marketingS",
  grade = 100,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // impression,
  loading = false,
  record,
  unlisted,
  title,
  CTAs,
  saved,
  summary,
  savable,
  images,
  disabled,
  className,
  onSaveToggle,
  onImagesModalOpen,
  interiorDesignerAction,
  children,
  loadingChildren,
  noData,
  ref,
}) => {
  const internalRef = useContentMenuSection<HTMLDivElement>({
    id: "summary",
    label: "Samenvatting",
    icon: "list",
    forcePosition: "leading",
  });

  return (
    <Section
      ref={mergeRefs(ref, internalRef)}
      className={mergeClasses(className)}
    >
      <Container size={container}>
        <Heading
          variant="main"
          title={title}
          description={
            loading ? (
              loadingChildren ? undefined : (
                <SummaryLoading />
              )
            ) : noData ? (
              <SummaryLoading />
            ) : (
              summary
            )
          }
        />

        {loading && loadingChildren}
        <Spacing />

        {noData && (
          <Message
            variant="default"
            body={
              unlisted
                ? `Huub kan deze woning op dit moment niet goed analyseren. Dat gebeurt soms wanneer het adres nog niet in de databronnen voorkomt of wanneer er te weinig informatie beschikbaar is. Is de woning net in de verkoop gegaan? Probeer het dan later nog eens.`
                : `Huub kan geen verkoopinformatie vinden voor deze woning. Het lijkt erop dat de woning momenteel niet te koop staat, waardoor Huub geen volledige analyse kan maken.`
            }
          />
        )}

        {!!record && (
          <HouseHeader
            record={record}
            savable={savable}
            saved={saved}
            onSaveToggle={onSaveToggle}
            disabled={disabled}
            titleTrailing={
              (interiorDesignerAction ||
                (images && images.length > 0 && !!onImagesModalOpen)) && (
                <>
                  {interiorDesignerAction}
                  {images && images.length > 0 && !!onImagesModalOpen && (
                    <>
                      <div className={photosIconButtonWrapperStyle}>
                        <IconButton
                          icon="image"
                          onClick={onImagesModalOpen}
                          variant="opaque"
                          border="default"
                          size="s"
                          aria-label={`Bekijk alle foto's (${images.length})`}
                        />
                        <Badge
                          value={images.length}
                          variant="secondary"
                          size="xs"
                          offset="small"
                        />
                      </div>
                      <div className={photosButtonWrapperStyle}>
                        <Button
                          onClick={onImagesModalOpen}
                          variant="opaque"
                          border="default"
                          size="s"
                          trailing={
                            <Badge
                              value={images.length}
                              variant="secondary"
                              size="xs"
                              inline
                            />
                          }
                        >
                          Bekijk alle foto's
                        </Button>
                      </div>
                    </>
                  )}
                </>
              )
            }
            card={
              record?.price != null && record.price > 0 ? (
                <Flex direction="column" gap="xxs" grow>
                  <Text variant="bodyS" bold>
                    {unlisted ? "Laatst bekende marktwaarde" : "Vraagprijs"}
                  </Text>
                  <Text variant="displayM" bold>
                    <ValueParser
                      value={{ type: "money", value: record.price }}
                    />
                  </Text>
                  {/* <Spacing grow /> */}
                  {/* {CTAs?.maximumMortgage} */}
                </Flex>
              ) : undefined
            }
            chanceCardCTA={noData ? undefined : CTAs?.chance}
          />
        )}
      </Container>

      <Container size={container}>
        {!noData && (
          <GaugeCard name="deze woning" grade={grade} loading={loading} />
        )}
        <Spacing />
      </Container>

      {children}
    </Section>
  );
};
